import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Permission, useHasPermission } from "@/auth/access-permissions";
import { Switch } from "@/components/design/Switch";
import { useRequestPowerQuiz } from "./use-request-power-quiz";
import { QuizRequestStatus } from "./ListingPowerQuizRequest";
import { Button } from "@/components/design/Button";
import { InviteExistingPersonDialog } from "@/components/invite/InviteExistingPersonDialog";
import {
  ListingApplicationState,
  PersonLoginInvitationOutcome,
  type ListingApplicationRowFragment,
  PersonLoginAction,
  PersonLoginAssignmentDocument,
  PersonLoginInvitationStep,
  PersonLoginStatus,
  type PersonLoginAssignmentFieldsFragment,
} from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

interface AccessProps { personId: string; application: ListingApplicationRowFragment; quizEnabled: boolean }
export function ListingApplicantAccess(props: AccessProps) {
  const permitted = useHasPermission(Permission.ProvisionInvitee);
  return permitted ? <ApplicantAccess {...props} /> : null;
}

function ApplicantAccess({ personId, application, quizEnabled }: AccessProps) {
  const [requestQuiz, setRequestQuiz] = useState(false);
  const quiz = useRequestPowerQuiz(application.id);
  const intl = useIntl();
  const [dialog, setDialog] = useState<PersonLoginAssignmentFieldsFragment | null>(null);
  const { data, loading, error, refetch } = useQuery(PersonLoginAssignmentDocument, {
    variables: { input: { personId } },
    fetchPolicy: "cache-and-network",
  });
  const assignment = data?.personLoginAssignment;
  const canInvite = !loading && !error && assignment?.status === PersonLoginStatus.Unassigned
    && assignment.actions.includes(PersonLoginAction.Invite);
  const canResend = !loading && !error && assignment?.actions.includes(PersonLoginAction.Resend);

  if (!loading && !error && !assignment) return null;

  return (
    <div className="mt-2 space-y-2 text-smycles-meta text-ink-muted">
      {error ? (
        <p role="alert">{userErrorMessage(intl, error)}</p>
      ) : loading && !assignment ? (
        <p aria-busy="true"><FormattedMessage id="share.listings.access.loading" defaultMessage="Zugang wird geladen…" /></p>
      ) : assignment ? (
        <>
          <p>
            {assignment.status === PersonLoginStatus.Assigned
              ? <FormattedMessage id="share.listings.access.assigned" defaultMessage="Login vorhanden" />
              : <FormattedMessage id="share.listings.access.unassigned" defaultMessage="Noch kein Login" />}
          </p>
          {assignment.invitationStep === PersonLoginInvitationStep.Sent ? (
            <p><FormattedMessage id="share.listings.access.sent" defaultMessage="Einladung versandt" /></p>
          ) : assignment.invitationStep === PersonLoginInvitationStep.SendFailed ? (
            <p><FormattedMessage id="share.listings.access.sendFailed" defaultMessage="Einladung nicht versandt" /></p>
          ) : assignment.invitationStep && assignment.invitationStep !== PersonLoginInvitationStep.Completed ? (
            <p><FormattedMessage id="share.listings.access.pending" defaultMessage="Einladung noch nicht abgeschlossen" /></p>
          ) : null}
          {canInvite || canResend ? (
            <Button variant="secondary" size="compact" onClick={() => setDialog(assignment)}>
              {canResend
                ? <FormattedMessage id="share.listings.access.resend" defaultMessage="Login-Einladung erneut senden" />
                : <FormattedMessage id="share.listings.access.invite" defaultMessage="Zum Login einladen" />}
            </Button>
          ) : null}
        </>
      ) : null}
      {dialog ? (
        <InviteExistingPersonDialog
          key={dialog.personId}
          assignment={dialog}
          onClose={() => setDialog(null)}
          onCompleted={(_assignment, outcome) => {
            void refetch().catch(() => undefined);
            if (requestQuiz && [PersonLoginInvitationOutcome.Invited, PersonLoginInvitationOutcome.Resent,
              PersonLoginInvitationOutcome.RecipientUpdated].includes(outcome)) void quiz.send();
          }}
          extraContent={({ busy, completed }) => quizEnabled && application.state !== ListingApplicationState.Rejected && application.state !== ListingApplicationState.Withdrawn ? <div className="mt-5 space-y-2 border-t border-ink/10 pt-4">
            <div className="flex items-center justify-between gap-4 text-smycles-body">
              <span>{intl.formatMessage({ id: "share.listings.quiz.withInvitation" })}</span>
              <Switch checked={requestQuiz} disabled={busy || completed || quiz.loading}
                onChange={setRequestQuiz} ariaLabel={intl.formatMessage({ id: "share.listings.quiz.withInvitation" })} />
            </div>
            <QuizRequestStatus request={application.powerQuizRequest} />
            {quiz.loading ? <p role="status">{intl.formatMessage({ id: "share.listings.quiz.sending" })}</p> : null}
            {quiz.error ? <p role="alert">{quiz.error}</p> : null}
          </div> : null}
        />
      ) : null}
    </div>
  );
}
